import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { useForm } from "react-hook-form"
import { useState, useEffect } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faAddressCard,
  faClock,
  faFileLines,
  faUser,
  faUserCheck,
  faUserGear
} from "@fortawesome/free-solid-svg-icons"
import api from "../../../services/api"

import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

import toastOptions from "../../../utils/toastOptions"
import { displayDateTime, handleFocus, numberMask } from "../../../utils/masks"
import { getValidator } from "../../../utils/validatorsYup"
import { utils } from "../../../utils/utils"

import DialogConfirm from "../../../components/DialogConfirm"
import Modal from "../../../components/modal"

interface Params {
  member: any
  open: boolean
  afterHandleSubmit: any
  afterCloseModal: any
}

export default function MemberCredencialResponse(props: Params) {
  const { member, open, afterHandleSubmit, afterCloseModal } = props

  const [modalIsOpen, setModelOpen] = useState<boolean>(false)
  const [openConfirm, setConfirmeOpen] = useState<boolean>(false)

  const schema = yup.object().shape({
    cardNumber: yup.string(),
    note: yup.string()
  })

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState
  } = useForm({
    resolver: yupResolver(schema)
  })

  useEffect(() => {
    setModelOpen(open)
  }, [member, open])

  const closeModal = () => {
    handleCloseModal()
  }

  const handleCloseModal = () => {
    setModelOpen(false)
    setConfirmeOpen(false)
    afterCloseModal()
  }

  const onSubmit = async () => {
    try {
      const { data } = await api.post(`/members/${member.id}/credential/print`)

      const name = `${member?.name.toLocaleLowerCase().split(" ")[0]}-credencial-${new Date().getTime()}.pdf`
      utils.printPDF(name, data.item, "application/pdf", true)
      setConfirmeOpen(true)
    } catch (e: any) {
      e?.messages?.forEach((m: string) => {
        toast.error(m, toastOptions)
      })
    }
  }

  const handleProcessCredential = async () => {
    await api.post(`/members/${member.id}/credential/response`, member)
    toast.success("Credencial entregue!", toastOptions)
    handleCloseModal()
    afterHandleSubmit()
  }

  const dialogClose = () => {
    setConfirmeOpen(false)
  }

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="modal-small"
        shouldCloseOnOverlayClick={false}
      >
        <div className="uk-modal-header">
          <div className="uk-flex uk-flex-between uk-flex-middle uk-grid-small" uk-grid="true">
            <div className="uk-width-expand">
              <span className="uk-text-meta uk-display-block">Fluxo de credencial</span>
              <h2 className="uk-modal-title uk-margin-remove-top uk-margin-remove-bottom uk-flex uk-flex-middle">
                <FontAwesomeIcon icon={faAddressCard} className="uk-margin-small-right" />
                Processar Carteirinha
              </h2>
            </div>
            <div className="uk-width-auto">
              <span className="uk-badge uk-badge-warning uk-padding-small uk-text-nowrap">
                Nº {member?.cardNumber || "PENDENTE"}
              </span>
            </div>
          </div>
        </div>

        <button
          className="uk-modal-close-default"
          uk-close="true"
          type="button"
          onClick={handleCloseModal}
        />

        <form onSubmit={handleSubmit<any>(onSubmit)} onReset={reset}>
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-3-5@s">
                <div className="uk-card uk-card-default uk-card-small uk-card-body uk-margin-small-bottom">
                  <h4 className="uk-heading-bullet uk-margin-small-bottom">Dados do Membro</h4>
                  <div className="uk-grid-small" uk-grid="true">
                    <div className="uk-width-1-1">
                      <label className="uk-form-label" htmlFor="name">
                        <FontAwesomeIcon icon={faUser} className="uk-margin-small-right" />
                        Nome
                      </label>
                      <input
                        id="name"
                        name="name"
                        className="uk-input uk-form-small"
                        defaultValue={member?.name}
                        ref={register}
                        disabled={true}
                      />
                    </div>

                    <div className="uk-width-1-2@s">
                      <label className="uk-form-label" htmlFor="ecclesiasticalFunction">
                        <FontAwesomeIcon icon={faUserGear} className="uk-margin-small-right" />
                        Função Eclesiástica
                      </label>
                      <input
                        id="ecclesiasticalFunction"
                        name="ecclesiasticalFunction"
                        className="uk-input uk-form-small"
                        defaultValue={member?.ecclesiasticalFunction?.description}
                        ref={register}
                        disabled={true}
                      />
                    </div>

                    <div className="uk-width-1-2@s">
                      <div className="uk-form-controls">
                        <label className="uk-form-label" htmlFor="cardNumber">
                          <FontAwesomeIcon icon={faAddressCard} className="uk-margin-small-right" />
                          Nº Carteirinha
                        </label>
                        <input
                          type="text"
                          id="cardNumber"
                          name="cardNumber"
                          className="uk-input uk-form-small"
                          defaultValue={member?.cardNumber}
                          ref={register}
                          onFocus={handleFocus}
                          autoComplete={"off"}
                          placeholder="999999"
                          onChange={(e) => {
                            e.target.value = numberMask(e.target.value)
                          }}
                          disabled={true}
                        />
                      </div>
                      <small className="uk-text-danger">{errors.cardNumber?.message}</small>
                    </div>

                    <div className="uk-width-1@s">
                      <label className="uk-form-label" htmlFor="note">
                        <FontAwesomeIcon icon={faFileLines} className="uk-margin-small-right" />
                        Observações
                      </label>
                      <textarea
                        id="note"
                        name="note"
                        className="uk-textarea uk-form-small uk-text-uppercase"
                        rows={4}
                        style={{ resize: "none" }}
                        defaultValue={member?.note}
                        autoComplete={"off"}
                        ref={register}
                      />
                      <small className="uk-text-danger">{errors.note?.message}</small>
                    </div>
                  </div>
                </div>

                <div
                  className="uk-card uk-card-default uk-card-small uk-card-body"
                  style={{ border: "1px dashed var(--border)", background: "var(--surface-muted)" }}
                >
                  <p className="uk-margin-remove uk-text-small uk-text-muted">
                    Confira os dados e emita a credencial. Depois da impressão, confirme a entrega.
                  </p>
                </div>
              </div>

              <div className="uk-width-2-5@s">
                <div
                  className="uk-card uk-card-default uk-card-small uk-card-body"
                  style={{ borderTop: "3px solid var(--warning)", background: "linear-gradient(180deg, #fffdf8 0%, #ffffff 100%)" }}
                >
                  <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
                    <h4 className="uk-margin-remove">Resumo da Solicitação</h4>
                    <span className="uk-label">Histórico</span>
                  </div>
                  <div className="uk-grid-small" uk-grid="true">
                    <div className="uk-width-1-1">
                      <label className="uk-form-label" htmlFor="reason">
                        <FontAwesomeIcon icon={faUserCheck} className="uk-margin-small-right" />
                        Motivo
                      </label>
                      <input
                        type="text"
                        id="reason"
                        name="reason"
                        disabled={true}
                        className="uk-input uk-form-small uk-text-bold"
                        defaultValue={member?.historic.reason}
                        ref={register}
                      />
                    </div>

                    <div className="uk-width-1-1">
                      <div className="uk-form-controls">
                        <label className="uk-form-label" htmlFor="requestedOn">
                          <FontAwesomeIcon icon={faClock} className="uk-margin-small-right" />
                          Solicitado em
                        </label>
                        <input
                          type="text"
                          id="requestedOn"
                          name="requestedOn"
                          disabled={true}
                          className="uk-input uk-form-small"
                          defaultValue={displayDateTime(member?.historic.createdDate)}
                          ref={register}
                        />
                      </div>
                    </div>

                    <div className="uk-width-1-1">
                      <div className="uk-form-controls">
                        <label className="uk-form-label" htmlFor="requestedBy">
                          <FontAwesomeIcon icon={faUser} className="uk-margin-small-right" />
                          Solicitado por
                        </label>
                        <input
                          type="text"
                          id="requestedBy"
                          name="requestedBy"
                          disabled={true}
                          className="uk-input uk-form-small"
                          defaultValue={member?.historic.createdBy}
                          ref={register}
                        />
                      </div>
                    </div>

                    <div className="uk-width-1-1">
                      <label className="uk-form-label" htmlFor="histNote">
                        Observações da Solicitação
                      </label>
                      <textarea
                        id="histNote"
                        name="histNote"
                        className="uk-textarea uk-form-small uk-text-uppercase"
                        rows={3}
                        style={{ resize: "none" }}
                        defaultValue={member?.historic.note}
                        disabled={true}
                        ref={register}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className="uk-margin-small-top uk-card uk-card-default uk-card-small uk-card-body"
                  style={{ background: "var(--surface-muted)" }}
                >
                  <div className="uk-flex uk-flex-middle">
                    <span className="uk-margin-small-right" uk-icon="icon: info"></span>
                    <small className="uk-text-small">
                      Se a impressão falhar, cancele, ajuste e emita novamente.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="uk-modal-footer">
            <div className="uk-grid-small uk-flex-center uk-text-center" uk-grid="true">
              <button
                className="uk-button uk-button-default uk-button-small uk-margin-small-right"
                onClick={handleCloseModal}
                disabled={formState.isSubmitting}
                type="button"
              >
                Cancelar
              </button>

              <button
                disabled={formState.isSubmitting}
                className="uk-button uk-button-primary uk-button-small uk-flex uk-flex-middle"
                type="submit"
              >
                {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                <span className="uk-margin-small-left">Emitir Credencial</span>
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <DialogConfirm
        open={openConfirm}
        text={[
          `A Credencial do membro ${member?.name} foi impressa corretamente?`,
          `Se sim clique em [Confirmar], se não clique em [Cancelar] e tente novamente.`
        ]}
        title="Credencial Impressa"
        handleConfirmation={handleProcessCredential}
        afterClose={dialogClose}
      ></DialogConfirm>
    </>
  )
}
